import type { DateKey, MonthKey } from '@kff/engine';
import { addMonths, daysBetween, endOfMonth } from './clients.js';
import { adsFee, agreementPackages, callOffPrices, findPackage, isFrameOnly, listPrice, minProductionsOf, PROJECT_DEFAULTS, type PriceListData, type QuoteTerms } from './pricing.js';

/**
 * Invoicing with Bokio (owner 2026-10-29, analys/bokio-fakturering-2026-10-29/forslag.md): the client's invoice details,
 * the monthly invoice basis the Hub builds from the signed agreement and the month's projects, and the figures on the
 * Finance page. Pure rules shared by the API and the web. Amounts are kronor excluding VAT unless a name says gross.
 */

/** The client's invoice details as the Hub keeps them (org no is the client's own column). */
export interface Billing {
  vatNo: string | null;
  line1: string | null;
  line2: string | null;
  postalCode: string | null;
  city: string | null;
  /** ISO 3166-1 alpha-2, "SE" when not set */
  country: string | null;
  /** where the invoice goes (the default contact in Bokio) */
  email: string | null;
  /** days ("30") or Bokio's free text */
  paymentTerms: string | null;
  /** the client's reference on every invoice (Bokio: buyerReference) */
  reference: string | null;
  /** the client's PO or order number (Bokio: orderNumberReference) */
  poNumber: string | null;
}

export const BILLING_FIELDS = ['vatNo', 'line1', 'line2', 'postalCode', 'city', 'country', 'email', 'paymentTerms', 'reference', 'poNumber'] as const satisfies readonly (keyof Billing)[];
/** The fields that live on the customer in Bokio too; reference and PO go on each invoice and stay in the Hub */
export const SYNCED_FIELDS = ['vatNo', 'line1', 'line2', 'postalCode', 'city', 'country', 'email', 'paymentTerms'] as const satisfies readonly (keyof Billing)[];

export const emptyBilling = (): Billing => ({ vatNo: null, line1: null, line2: null, postalCode: null, city: null, country: null, email: null, paymentTerms: null, reference: null, poNumber: null });

const t = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v.trim() : null);

/** Any object into Billing: unknown keys dropped, blanks null, text capped. */
export function cleanBilling(raw: unknown): Billing {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const b = emptyBilling();
  for (const k of BILLING_FIELDS) b[k] = t(o[k])?.slice(0, 200) ?? null;
  if (b.country) b.country = b.country.toUpperCase().slice(0, 2);
  if (b.email) b.email = b.email.toLowerCase();
  return b;
}

/** What an invoice needs before it can go to Bokio (spec FLOW-217: no invoice without invoice details). */
export function billingMissing(orgNo: string | null | undefined, b: Billing | null): ('orgNo' | 'line1' | 'postalCode' | 'city')[] {
  const out: ('orgNo' | 'line1' | 'postalCode' | 'city')[] = [];
  if (!t(orgNo)) out.push('orgNo');
  if (!b?.line1) out.push('line1');
  if (!b?.postalCode) out.push('postalCode');
  if (!b?.city) out.push('city');
  return out;
}

/** The synced fields that differ between two versions (country "SE" and blank are the same). */
export function billingDiff(a: Partial<Billing> & { orgNo?: string | null }, b: Partial<Billing> & { orgNo?: string | null }): string[] {
  const norm = (k: string, v: unknown) => {
    const s = t(v);
    if (!s) return k === 'country' ? 'SE' : '';
    if (k === 'orgNo' || k === 'vatNo' || k === 'postalCode') return s.replace(/[\s-]/g, '').toUpperCase();
    return k === 'email' ? s.toLowerCase() : s;
  };
  return (['orgNo', ...SYNCED_FIELDS] as string[]).filter((k) => norm(k, (a as Record<string, unknown>)[k]) !== norm(k, (b as Record<string, unknown>)[k]));
}

/**
 * Suggestions from the free text Monday kept (0049: "invoice_info" and "address"): VAT no, e-mail, postcode and town,
 * street, reference, PO and payment terms. Only suggestions; a person confirms before anything is saved.
 */
export function parseInvoiceText(invoiceInfo: string | null | undefined, address: string | null | undefined): Partial<Billing> {
  const text = [address ?? '', invoiceInfo ?? ''].join('\n');
  const out: Partial<Billing> = {};
  const vat = /\bSE\s?(\d{6})-?(\d{4})\s?01\b/i.exec(text);
  if (vat) out.vatNo = `SE${vat[1]}${vat[2]}01`;
  const mail = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.exec(invoiceInfo ?? '') ?? /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i.exec(text);
  if (mail) out.email = mail[0].toLowerCase();
  for (const src of [address ?? '', invoiceInfo ?? '']) {
    const parts = src.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
    const i = parts.findIndex((x) => /^(SE-?)?\d{3}\s?\d{2}\s+\S/i.test(x));
    if (i < 0) continue;
    const m = /^(?:SE-?)?(\d{3})\s?(\d{2})\s+(.+)$/i.exec(parts[i]!)!;
    out.postalCode = `${m[1]} ${m[2]}`;
    out.city = m[3]!.trim();
    const street = parts.slice(0, i).filter((x) => !/@|^ref|^att|^org|^moms|^vat|^SE\d|^\d{6}-?\d{4}$/i.test(x));
    if (street.length) out.line1 = street[street.length - 1]!;
    if (street.length > 1) out.line2 = street[street.length - 2]!;
    break;
  }
  const ref = /\b(?:er\s+)?ref(?:erens)?\.?\s*[:-]?\s*([^\n,;]{2,60})/i.exec(invoiceInfo ?? '');
  if (ref) out.reference = ref[1]!.trim();
  const po = /\b(?:PO|inköpsorder|ordernummer|order\s*nr)\.?\s*[:#-]?\s*([A-Z0-9][A-Z0-9-/]{1,30})/i.exec(invoiceInfo ?? '');
  if (po) out.poNumber = po[1]!;
  const days = /\b(\d{2,3})\s*(?:dagar|days|d\.?\s*netto|dgr)\b/i.exec(invoiceInfo ?? '');
  if (days) out.paymentTerms = days[1]!;
  if (out.postalCode && !out.country) out.country = 'SE';
  return out;
}

// ---- the invoice basis ----

export const BASIS_UNITS = ['month', 'production', 'once', 'h', 'piece'] as const;
export type BasisUnit = (typeof BASIS_UNITS)[number];
export const VAT_RATES = [25, 12, 6, 0] as const;
export type VatRate = (typeof VAT_RATES)[number];

export interface BasisLine {
  /** stable key: auto lines are matched on it when the basis is refreshed ("fee:<agreement>", "project:<id>", "m:<random>") */
  key: string;
  source: 'auto' | 'manual';
  text: string;
  qty: number;
  unit: BasisUnit;
  /** null = no price found (the producer sets it before confirming) */
  unitPrice: number | null;
  vat: VatRate;
  /** the price list entry the line is (package key, add-on key, "startup" or "ads"), for the article in Bokio */
  itemKey?: string | null;
  projectId?: string | null;
  agreementId?: string | null;
  /** a trip's lines (travel.ts): the shoot they are for, so the shoot is not suggested again */
  shootId?: string | null;
  /** an auto line a person changed: kept as it is when the basis is refreshed */
  edited?: boolean;
  /** an auto line a person took away: kept struck through, not invoiced */
  excluded?: boolean;
}

export interface BasisAgreement {
  id: string;
  ref: string;
  kind: 'subscription' | 'project';
  terms: QuoteTerms;
  status: string;
  signedOn: DateKey | null;
}

export interface BasisProject {
  id: string;
  code: string;
  name: string;
  kind: 'retainer_month' | 'extra' | 'one_off' | 'internal';
  creativePackage: string | null;
  deliveryMonth: MonthKey;
  cancelled: boolean;
  /** the client's reference or PO for the project (spec.reference, owner 2026-11-01), shown on its line */
  reference?: string | null;
}

export interface BasisInput {
  month: MonthKey;
  priceList: PriceListData;
  agreements: BasisAgreement[];
  projects: BasisProject[];
  /** The client's projects over the periods of its project agreements, for the minimum (default: `projects`) */
  periodProjects?: BasisProject[];
  /** All Client Data's agreement (0050) for a client with no agreement signed in the Hub */
  mondayContract?: { package: string | null; mrr: number | null; start: string | null; end: string | null } | null;
  /**
   * An approved Monday agreement's package prices, for a client with no agreement signed in the Hub: per month (a
   * subscription: its monthly projects) or per production (every project)
   */
  agreed?: { perMonth: boolean; prices: Record<string, number> } | null;
}

const MONTHS_SV = ['januari', 'februari', 'mars', 'april', 'maj', 'juni', 'juli', 'augusti', 'september', 'oktober', 'november', 'december'];
const MONTHS_EN = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
/** "oktober 2026" / "October 2026" */
export const monthName = (m: MonthKey, lang: 'sv' | 'en' = 'sv') => `${(lang === 'sv' ? MONTHS_SV : MONTHS_EN)[Number(m.slice(5, 7)) - 1]} ${m.slice(0, 4)}`;

const startOf = (a: BasisAgreement) => a.terms.startDate ?? a.signedOn ?? '';

/** The agreement that applies in the month: the newest signed one that has started by the month's end. */
export function agreementInForce(agreements: BasisAgreement[], month: MonthKey): BasisAgreement | null {
  const last = endOfMonth(`${month}-01`);
  return (
    agreements
      .filter((a) => a.status === 'signed' && startOf(a) && startOf(a) <= last)
      .sort((x, y) => startOf(y).localeCompare(startOf(x)))[0] ?? null
  );
}

/** A project's price: the agreement's call-off price for its package, else the minimum package's, else the list price for one production. */
function projectPrice(pl: PriceListData, a: BasisAgreement | null, pkgKey: string | null): { price: number | null; itemKey: string | null } {
  if (a?.kind === 'project') {
    const offs = callOffPrices(pl, a.terms);
    const hit = offs.find((o) => o.key === pkgKey) ?? offs.find((o) => o.minimum) ?? null;
    if (hit) return { price: hit.price, itemKey: hit.key };
  }
  if (!pkgKey) return { price: null, itemKey: null };
  const key = findPackage(pl, pkgKey)?.key ?? pkgKey;
  return { price: listPrice(pl, 'project', key, 1).price, itemKey: findPackage(pl, pkgKey) ? key : null };
}

/**
 * A project agreement's minimum over its period (owner 2026-10-29: "min antal projekt över en tid"): how many productions
 * were called off (delivered in the period, not cancelled) against the minimum, and the month the period ends, when any
 * shortfall is billed (Projekt v4 § Minimiåtagande vid periodens slut). Null for a subscription or a frame agreement.
 */
export interface MinimumProgress {
  min: number;
  done: number;
  missing: number;
  start: DateKey;
  /** the period's last day */
  end: DateKey;
  endMonth: MonthKey;
}
export function minimumProgress(a: Pick<BasisAgreement, 'kind' | 'terms' | 'signedOn'>, projects: Pick<BasisProject, 'deliveryMonth' | 'cancelled' | 'kind'>[]): MinimumProgress | null {
  const start = a.terms.startDate ?? a.signedOn;
  if (a.kind !== 'project' || isFrameOnly(a.terms) || !start) return null;
  const min = minProductionsOf(a.terms);
  const after = addMonths(start, a.terms.periodMonths ?? PROJECT_DEFAULTS.periodMonths);
  // the day before: a period from 1 Nov for 12 months ends 31 Oct
  const end = new Date(Date.parse(`${after}T00:00:00Z`) - 86_400_000).toISOString().slice(0, 10);
  const from = start.slice(0, 7);
  const to = end.slice(0, 7);
  const done = projects.filter((p) => !p.cancelled && p.kind !== 'internal' && p.deliveryMonth >= from && p.deliveryMonth <= to).length;
  return { min, done, missing: Math.max(0, min - done), start, end, endMonth: to as MonthKey };
}

/** A subscription's package and fee in the month: the monthly project's package when it has one the agreement lists. */
export function monthPackage(input: Pick<BasisInput, 'month' | 'priceList' | 'projects'>, a: BasisAgreement): { key: string; price: number | null; switched: boolean } {
  const pl = input.priceList;
  const own = findPackage(pl, a.terms.packageKey)?.key ?? a.terms.packageKey;
  const pkgs = agreementPackages(pl, a.terms);
  const mp = input.projects.find((p) => p.kind === 'retainer_month' && p.deliveryMonth === input.month && !p.cancelled && p.creativePackage);
  const chosen = mp ? pkgs.find((x) => x.key === (findPackage(pl, mp.creativePackage)?.key ?? mp.creativePackage)) : undefined;
  if (chosen && chosen.key !== own) return { key: chosen.key, price: chosen.price, switched: true };
  return { key: own, price: a.terms.price ?? listPrice(pl, 'subscription', a.terms.packageKey).price, switched: false };
}

/** What the Hub knows the client should pay for in the month. */
export function autoLines(input: BasisInput): BasisLine[] {
  const { month, priceList: pl } = input;
  const out: BasisLine[] = [];
  const a = agreementInForce(input.agreements, month);
  const mname = monthName(month);
  if (a) {
    if (a.kind === 'subscription') {
      // the month's package (owner 2026-10-30: the client can switch package every month): the one on the client's monthly
      // project, at the agreement's price for it; the agreement's own package when the month has no project with one
      const fee = monthPackage(input, a);
      const pkg = findPackage(pl, fee.key);
      const name = pkg ? `${pkg.key} (${pkg.sv})` : fee.key;
      out.push({ key: `fee:${a.id}`, source: 'auto', text: `${name} – månadsavgift ${mname}`, qty: 1, unit: 'month', unitPrice: fee.price, vat: 25, itemKey: pkg?.key ?? null, agreementId: a.id });
      if (a.terms.adsMediaBudget !== null && a.terms.adsMediaBudget !== undefined && a.terms.adsMediaBudget >= 0)
        out.push({ key: `ads:${a.id}`, source: 'auto', text: `Annonshantering ${mname}`, qty: 1, unit: 'month', unitPrice: adsFee(pl, a.terms.adsMediaBudget), vat: 25, itemKey: 'ads', agreementId: a.id });
    }
    if ((a.terms.startupFee ?? 0) > 0 && startOf(a).slice(0, 7) === month)
      out.push({ key: `startup:${a.id}`, source: 'auto', text: 'Startavgift', qty: 1, unit: 'once', unitPrice: a.terms.startupFee, vat: 25, itemKey: 'startup', agreementId: a.id });
  }
  const delivered = input.projects.filter((p) => p.deliveryMonth === month && !p.cancelled && p.kind !== 'internal');
  if (!a) {
    // Monday's MRR is what the client is expected to bring in a month, not a fee of its own (owner 2026-10-29: "abonnemangskostnaden
    // är inte separat, det är projektkostnaden"): the month's projects are the invoice. Only a running deal with nothing
    // delivered in the month gets the MRR as a line, for the producer to keep or remove.
    const mc = input.mondayContract;
    const first = `${month}-01`;
    const running = Boolean(mc?.package && (/^ongoing$/i.test(mc.package) || /^\d+\s*months?$/i.test(mc.package)));
    if (mc?.mrr && mc.mrr > 0 && running && !delivered.length && (!mc.start || mc.start <= endOfMonth(first)) && (!mc.end || mc.end >= first))
      out.push({ key: 'fee:monday', source: 'auto', text: `Månadsbelopp enligt Monday-avtalet (${mc.package}) ${mname}`, qty: 1, unit: 'month', unitPrice: mc.mrr, vat: 25, itemKey: null });
  }
  // a project agreement whose period ends this month with fewer call-offs than the minimum: the shortfall, at the minimum package's price
  for (const g of input.agreements) {
    if (g.status !== 'signed') continue;
    const m = minimumProgress(g, input.periodProjects ?? input.projects);
    if (!m || m.endMonth !== month || m.missing === 0) continue;
    const minPkg = callOffPrices(pl, g.terms).find((o) => o.minimum);
    out.push({
      key: `shortfall:${g.id}`,
      source: 'auto',
      text: `Mellanskillnad minimiåtagande: ${m.done} av ${m.min} produktioner avropade ${m.start}–${m.end}${minPkg ? ` (${minPkg.key})` : ''}`,
      qty: m.missing,
      unit: 'production',
      unitPrice: minPkg?.price ?? null,
      vat: 25,
      itemKey: minPkg?.key ?? null,
      agreementId: g.id
    });
  }
  const feePackage = a?.kind === 'subscription' ? monthPackage(input, a).key : null;
  for (const p of delivered) {
    // the subscription's own package (and its monthly productions) is what the fee pays for
    if (a?.kind === 'subscription' && (p.kind === 'retainer_month' || (feePackage && (findPackage(pl, p.creativePackage)?.key ?? p.creativePackage) === feePackage))) continue;
    const pkg = findPackage(pl, p.creativePackage);
    const agreed = !a && input.agreed && (!input.agreed.perMonth || p.kind === 'retainer_month') ? input.agreed.prices[pkg?.key ?? p.creativePackage ?? ''] : undefined;
    const { price, itemKey } = agreed ? { price: agreed, itemKey: pkg?.key ?? null } : projectPrice(pl, a, p.creativePackage);
    out.push({
      key: `project:${p.id}`,
      source: 'auto',
      text: `${p.name}${pkg ? ` – ${pkg.key}` : ''}${p.reference ? ` (ref. ${p.reference})` : ''}`,
      qty: 1,
      unit: 'production',
      unitPrice: price,
      vat: 25,
      itemKey,
      projectId: p.id,
      agreementId: a?.id ?? null
    });
  }
  return out;
}

/**
 * A refreshed basis: auto lines take the latest from the agreement and projects, except those a person changed or took
 * away; lines a person added stay; auto lines whose source is gone are dropped unless a person changed them.
 */
export function mergeLines(current: BasisLine[], fresh: BasisLine[]): BasisLine[] {
  const byKey = new Map(current.map((l) => [l.key, l]));
  const out: BasisLine[] = [];
  for (const f of fresh) {
    const c = byKey.get(f.key);
    out.push(c && (c.edited || c.excluded) ? c : f);
    byKey.delete(f.key);
  }
  for (const c of current) if (byKey.has(c.key) && (c.source === 'manual' || c.edited)) out.push(c);
  return out;
}

export const lineNet = (l: BasisLine): number => (l.excluded || l.unitPrice === null ? 0 : Math.round(l.qty * l.unitPrice * 100) / 100);

export interface BasisTotal {
  net: number;
  vat: number;
  gross: number;
  /** lines that would be invoiced but have no price */
  unpriced: number;
  lines: number;
}

export function basisTotal(lines: BasisLine[]): BasisTotal {
  const live = lines.filter((l) => !l.excluded);
  const net = live.reduce((s, l) => s + lineNet(l), 0);
  const vat = live.reduce((s, l) => s + (lineNet(l) * l.vat) / 100, 0);
  return { net: Math.round(net * 100) / 100, vat: Math.round(vat * 100) / 100, gross: Math.round((net + vat) * 100) / 100, unpriced: live.filter((l) => l.unitPrice === null).length, lines: live.length };
}

/** A line as a person writes it: text, quantity, price and VAT checked and capped. */
export function cleanLine(raw: unknown, key: string): BasisLine | null {
  const o = (raw && typeof raw === 'object' ? raw : {}) as Record<string, unknown>;
  const text = t(o.text)?.slice(0, 200);
  const qty = Number(o.qty);
  const price = o.unitPrice === null || o.unitPrice === '' || o.unitPrice === undefined ? null : Number(o.unitPrice);
  const vat = Number(o.vat ?? 25);
  const unit = (BASIS_UNITS as readonly string[]).includes(String(o.unit)) ? (o.unit as BasisUnit) : 'piece';
  if (!text || !Number.isFinite(qty) || qty <= 0 || qty > 10_000) return null;
  if (price !== null && (!Number.isFinite(price) || Math.abs(price) > 10_000_000)) return null;
  if (!(VAT_RATES as readonly number[]).includes(vat)) return null;
  const id = (v: unknown) => (typeof v === 'string' && ID.test(v) ? v : null);
  const line: BasisLine = { key, source: 'manual', text, qty: Math.round(qty * 100) / 100, unit, unitPrice: price === null ? null : Math.round(price * 100) / 100, vat: vat as VatRate };
  // a trip (travel.ts) keeps what it is for: the project's margin and the shoot it was suggested from
  if (o.itemKey === 'travel' || o.itemKey === 'travel_time') line.itemKey = o.itemKey;
  if (id(o.projectId)) line.projectId = id(o.projectId);
  if (id(o.shootId)) line.shootId = id(o.shootId);
  return line;
}
const ID = /^[\w-]{1,64}$/;

// ---- the Finance page ----

/** Bokio's invoice statuses (draft until published; credit = a credit note) */
export type BokioStatus = 'draft' | 'published' | 'paid' | 'overPaid' | 'underPaid' | 'overdue' | 'credited' | 'credit';

export interface FinanceInvoice {
  id: string;
  clientId: string | null;
  customerName: string | null;
  number: string | null;
  status: BokioStatus | string;
  invoiceDate: DateKey | null;
  dueDate: DateKey | null;
  /** gross, VAT included */
  total: number;
  totalTax: number;
  paid: number;
}

/** Sent and still owed something */
export const isOpen = (i: Pick<FinanceInvoice, 'status'>) => i.status === 'published' || i.status === 'overdue' || i.status === 'underPaid';
/** Counts as invoiced: everything sent except drafts, credited invoices and credit notes */
export const isInvoiced = (i: Pick<FinanceInvoice, 'status'>) => i.status !== 'draft' && i.status !== 'credited' && i.status !== 'credit';
export const isPaid = (i: Pick<FinanceInvoice, 'status'>) => i.status === 'paid' || i.status === 'overPaid';
export const netOf = (i: Pick<FinanceInvoice, 'total' | 'totalTax'>) => Math.round((i.total - i.totalTax) * 100) / 100;
export const owedOf = (i: Pick<FinanceInvoice, 'total' | 'paid'>) => Math.max(0, Math.round((i.total - i.paid) * 100) / 100);
export const isOverdue = (i: FinanceInvoice, today: DateKey) => isOpen(i) && (i.status === 'overdue' || Boolean(i.dueDate && i.dueDate < today));

export interface FinanceOverview {
  month: MonthKey;
  /** invoiced this month, excl. VAT */
  invoiced: number;
  invoicedCount: number;
  /** owed on sent invoices, incl. VAT */
  outstanding: number;
  outstandingCount: number;
  overdue: number;
  overdueCount: number;
  /** the last 12 months, excl. VAT, oldest first */
  series: { month: MonthKey; paid: number; unpaid: number }[];
  /** owed incl. VAT by days past due */
  aging: { notDue: number; d30: number; d60: number; older: number };
  /** the largest clients the last 12 months, excl. VAT */
  top: { clientId: string | null; name: string; net: number; count: number }[];
}

export function financeOverview(invoices: FinanceInvoice[], today: DateKey, names: Record<string, string> = {}): FinanceOverview {
  const month = today.slice(0, 7);
  const months = Array.from({ length: 12 }, (_, i) => addMonths(`${month}-01`, i - 11).slice(0, 7));
  const from = months[0]!;
  const sent = invoices.filter(isInvoiced);
  const thisMonth = sent.filter((i) => i.invoiceDate?.slice(0, 7) === month);
  const open = invoices.filter(isOpen);
  const late = open.filter((i) => isOverdue(i, today));
  const r = (n: number) => Math.round(n * 100) / 100;
  const aging = { notDue: 0, d30: 0, d60: 0, older: 0 };
  for (const i of open) {
    const days = i.dueDate ? daysBetween(i.dueDate, today) : 0;
    const k = days <= 0 && i.status !== 'overdue' ? 'notDue' : days <= 30 ? 'd30' : days <= 60 ? 'd60' : 'older';
    aging[k] = r(aging[k] + owedOf(i));
  }
  const top = new Map<string, { clientId: string | null; name: string; net: number; count: number }>();
  for (const i of sent.filter((x) => (x.invoiceDate ?? '') >= `${from}-01`)) {
    const k = i.clientId ?? `n:${i.customerName ?? '?'}`;
    const cur = top.get(k) ?? { clientId: i.clientId, name: (i.clientId && names[i.clientId]) || i.customerName || '–', net: 0, count: 0 };
    cur.net = r(cur.net + netOf(i));
    cur.count += 1;
    top.set(k, cur);
  }
  return {
    month,
    invoiced: r(thisMonth.reduce((s, i) => s + netOf(i), 0)),
    invoicedCount: thisMonth.length,
    outstanding: r(open.reduce((s, i) => s + owedOf(i), 0)),
    outstandingCount: open.length,
    overdue: r(late.reduce((s, i) => s + owedOf(i), 0)),
    overdueCount: late.length,
    series: months.map((m) => {
      const inM = sent.filter((i) => i.invoiceDate?.slice(0, 7) === m);
      return { month: m, paid: r(inM.filter(isPaid).reduce((s, i) => s + netOf(i), 0)), unpaid: r(inM.filter((i) => !isPaid(i)).reduce((s, i) => s + netOf(i), 0)) };
    }),
    aging,
    top: [...top.values()].sort((x, y) => y.net - x.net).slice(0, 8)
  };
}

// ---- supplier invoices and the margin (owner 2026-10-29: "Ja, med attest och marginal") ----

const r2 = (n: number) => Math.round(n * 100) / 100;

/** Excluding VAT from Bokio's lines; without lines (a preview field in Bokio) the total at 25 % VAT, marked estimated. */
export function supplierNet(lines: { qty: number; unitPrice: number }[], total: number): { net: number; estimated: boolean } {
  const fromLines = lines.reduce((s, l) => s + l.qty * l.unitPrice, 0);
  return lines.length && fromLines ? { net: r2(fromLines), estimated: false } : { net: r2(total / 1.25), estimated: true };
}

export interface SuggestProject {
  id: string;
  code: string;
  name: string;
  clientName: string;
  deliveryMonth: MonthKey;
  closed: boolean;
  /** the names of the people on the project (a freelancer's invoice comes from their name or company) */
  people: string[];
  /** the actors with a signed model release on the project (actors are paid on invoice, owner 2026-10-29) */
  actors?: ActorBilling[];
}
export interface SuggestInvoice {
  supplierId: string | null;
  supplierName: string | null;
  supplierOrgNo?: string | null;
  number: string | null;
  invoiceDate: DateKey | null;
  texts: string[];
}
export interface ProjectSuggestion {
  projectId: string;
  why: 'code' | 'name' | 'person' | 'actor' | 'client' | 'before';
}

/** A name folded for matching: lower case, no accents, no company form (AB, HB …), words only. */
export const fold = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\b(ab|aktiebolag|hb|kb|enskild firma|firma)\b/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
/** Whether an invoice from this supplier is from this person (or their company): every word of the person's name is in it. */
export function supplierIs(supplierName: string | null, personName: string): boolean {
  const sup = ` ${fold(supplierName ?? '')} `;
  const words = fold(personName).split(' ').filter((w) => w.length >= 2);
  return words.length >= 2 ? words.every((w) => sup.includes(` ${w} `)) : words.length === 1 && words[0]!.length >= 4 && sup.includes(` ${words[0]} `);
}
/** How an actor is named on the release: the release's reference and the company they invoice through (0058). */
export interface ActorBilling {
  name: string;
  invoiceRef?: string | null;
  billingName?: string | null;
  billingOrgNo?: string | null;
}
const digits = (s: string | null | undefined) => (s ?? '').replace(/\D/g, '');
/**
 * Whether a supplier invoice is from this actor, and how: the release's reference in the invoice number or text (KFF-A-1001,
 * spaces and dashes optional), the company's org.nr, the company's name, or the actor's own name.
 */
export function actorInvoiceMatch(inv: { supplierName: string | null; supplierOrgNo?: string | null; number?: string | null; texts?: string[] }, a: ActorBilling): 'ref' | 'org' | 'company' | 'name' | null {
  const ref = digits(a.invoiceRef);
  if (ref) {
    const text = [inv.number ?? '', ...(inv.texts ?? [])].join(' ').toUpperCase();
    if (new RegExp(`KFF\\s*-?\\s*A\\s*-?\\s*${ref}(?!\\d)`).test(text)) return 'ref';
  }
  const org = digits(a.billingOrgNo);
  if (org.length >= 10 && digits(inv.supplierOrgNo).endsWith(org.slice(-10))) return 'org';
  if (a.billingName && fold(a.billingName).length >= 3 && fold(inv.supplierName ?? '') === fold(a.billingName)) return 'company';
  return supplierIs(inv.supplierName, a.name) ? 'name' : null;
}
const monthsApart = (a: MonthKey, b: MonthKey) => Math.abs((+a.slice(0, 4) - +b.slice(0, 4)) * 12 + (+a.slice(5, 7) - +b.slice(5, 7)));

/**
 * Up to three projects an invoice probably belongs to, best first: the project's code or name in the invoice text, the
 * supplier being someone on the team that month, the client's name in the text, or the project this supplier's last
 * invoice went to. Only a suggestion: a person picks.
 */
export function suggestProjects(inv: SuggestInvoice, projects: SuggestProject[], history: { supplierId: string | null; projectId: string; invoiceDate: DateKey | null }[]): ProjectSuggestion[] {
  const text = ` ${fold([inv.number ?? '', ...inv.texts].join(' '))} `;
  const raw = [inv.number ?? '', ...inv.texts].join(' ').toLowerCase();
  const supplier = fold(inv.supplierName ?? '');
  const month = (inv.invoiceDate ?? '').slice(0, 7);
  const scored: { p: SuggestProject; score: number; why: ProjectSuggestion['why'] }[] = [];
  const last = history.filter((h) => h.supplierId && h.supplierId === inv.supplierId).sort((a, b) => (b.invoiceDate ?? '').localeCompare(a.invoiceDate ?? ''))[0];
  for (const p of projects) {
    const near = month ? monthsApart(p.deliveryMonth, month) : 0;
    let score = 0;
    let why = 'client' as ProjectSuggestion['why'];
    const add = (n: number, w: ProjectSuggestion['why']) => {
      if (n > score) [score, why] = [n, w];
    };
    if (p.code && raw.includes(p.code.toLowerCase())) add(100, 'code');
    const name = fold(p.name);
    if (name.length >= 5 && text.includes(` ${name} `)) add(60 - near * 5, 'name');
    if (supplier && near <= 2 && p.people.some((n) => fold(n).length >= 5 && (supplier.includes(fold(n)) || fold(n).includes(supplier)))) add(45 - near * 10, 'person');
    for (const a of p.actors ?? []) {
      const how = actorInvoiceMatch({ supplierName: inv.supplierName, supplierOrgNo: inv.supplierOrgNo, number: inv.number, texts: inv.texts }, a);
      // the release's reference is as good as the project code; org.nr and company nearly so; a name only near the month
      if (how === 'ref') add(100, 'actor');
      else if (how === 'org' || how === 'company') add(near <= 3 ? 70 : 40, 'actor');
      else if (how === 'name' && near <= 2) add(55 - near * 10, 'actor');
    }
    const client = fold(p.clientName);
    if (client.length >= 3 && text.includes(` ${client} `) && near <= 2) add(35 - near * 5, 'client');
    if (last?.projectId === p.id && !p.closed) add(30, 'before');
    if (p.closed && why !== 'code') score -= 15;
    if (score >= 25) scored.push({ p, score, why });
  }
  return scored
    .sort((a, b) => b.score - a.score || monthsApart(a.p.deliveryMonth, month || a.p.deliveryMonth) - monthsApart(b.p.deliveryMonth, month || b.p.deliveryMonth))
    .slice(0, 3)
    .map((x) => ({ projectId: x.p.id, why: x.why }));
}

export interface MarginRow {
  /** excluding VAT */
  revenue: number;
  cost: number;
  margin: number;
  /** margin / revenue, null without revenue */
  pct: number | null;
}
const row = (revenue: number, cost: number): MarginRow => ({ revenue: r2(revenue), cost: r2(cost), margin: r2(revenue - cost), pct: revenue > 0 ? Math.round(((revenue - cost) / revenue) * 1000) / 10 : null });

/**
 * Margin per project and client over a period. A project's revenue is the invoice-basis lines tied to it that reached
 * Bokio; a client's revenue is everything invoiced to it (subscription fees included), so a subscription client's projects
 * can show cost without revenue while the client as a whole carries both. Costs are attested or waiting supplier invoices
 * on the client's projects; overhead and untied invoices are totals of their own.
 */
export function margins(input: {
  projects: { id: string; clientId: string }[];
  /** invoice-basis lines that reached Bokio */
  basisLines: { projectId: string | null; net: number }[];
  invoices: { clientId: string | null; net: number }[];
  costs: { projectId: string | null; overhead: boolean; net: number }[];
  /** the project costing's own cost: time × internal rate and signed actor fees not invoiced yet */
  projectCosts?: { projectId: string; net: number }[];
}): { projects: Map<string, MarginRow>; clients: Map<string, MarginRow>; overhead: number; untied: number; total: MarginRow } {
  const clientOf = new Map(input.projects.map((p) => [p.id, p.clientId]));
  const pRev = new Map<string, number>();
  const pCost = new Map<string, number>();
  const cRev = new Map<string, number>();
  const cCost = new Map<string, number>();
  const plus = (m: Map<string, number>, k: string, n: number) => m.set(k, (m.get(k) ?? 0) + n);
  for (const l of input.basisLines) if (l.projectId && clientOf.has(l.projectId)) plus(pRev, l.projectId, l.net);
  for (const i of input.invoices) if (i.clientId) plus(cRev, i.clientId, i.net);
  let overhead = 0;
  let untied = 0;
  for (const c of input.costs) {
    if (c.overhead) overhead += c.net;
    else if (c.projectId && clientOf.has(c.projectId)) {
      plus(pCost, c.projectId, c.net);
      plus(cCost, clientOf.get(c.projectId)!, c.net);
    } else untied += c.net;
  }
  for (const c of input.projectCosts ?? []) {
    if (!clientOf.has(c.projectId) || !c.net) continue;
    plus(pCost, c.projectId, c.net);
    plus(cCost, clientOf.get(c.projectId)!, c.net);
  }
  const projects = new Map([...new Set([...pRev.keys(), ...pCost.keys()])].map((k) => [k, row(pRev.get(k) ?? 0, pCost.get(k) ?? 0)]));
  const clients = new Map([...new Set([...cRev.keys(), ...cCost.keys()])].map((k) => [k, row(cRev.get(k) ?? 0, cCost.get(k) ?? 0)]));
  const revenue = [...cRev.values()].reduce((s, n) => s + n, 0);
  const cost = [...cCost.values()].reduce((s, n) => s + n, 0) + overhead + untied;
  return { projects, clients, overhead: r2(overhead), untied: r2(untied), total: row(revenue, cost) };
}

/**
 * Subscription revenue on projects (owner 2026-10-29: "Varför visar inte abonnemang intäkt?"): what a client was invoiced
 * in a month beyond the lines tied to a project (the monthly fee) is shared over the client's projects delivered that
 * month, weighted by the package's list price (1 when unknown). A month with no project keeps it on the client.
 */
export function allocateSubscriptions(input: {
  /** per invoice: the client, the month it is for (the basis period, else the invoice month) and what is not tied to a project */
  untied: { clientId: string; month: MonthKey; net: number }[];
  projects: { id: string; clientId: string; month: MonthKey; weight: number }[];
}): { projectId: string; net: number }[] {
  const pot = new Map<string, number>();
  for (const u of input.untied) if (u.net > 0) pot.set(`${u.clientId}|${u.month}`, (pot.get(`${u.clientId}|${u.month}`) ?? 0) + u.net);
  const out: { projectId: string; net: number }[] = [];
  for (const [key, net] of pot) {
    const mine = input.projects.filter((p) => `${p.clientId}|${p.month}` === key);
    const sum = mine.reduce((s, p) => s + (p.weight > 0 ? p.weight : 1), 0);
    if (!sum) continue;
    for (const p of mine) out.push({ projectId: p.id, net: r2((net * (p.weight > 0 ? p.weight : 1)) / sum) });
  }
  return out;
}
